<script setup lang="ts">
import { api } from '@/api';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { routenames } from '@/router/routenames';
import { type NumericQuantityDto } from '@/api/generator';
import QuantityForm from './components/QuantityForm.vue';
import { useNotificationStore } from '@/stores/notificationStore';
import { toCreateUpdateNumericQuantityDto } from './mappings.ts';
import { NIL } from 'uuid';
import { useTranslation } from 'i18next-vue';
import { useRouter } from 'vue-router';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { backendValidator } from '@/validation/validators/backendValidator.ts';

const { t } = useTranslation();

const router = useRouter();
const notificationStore = useNotificationStore();

const initialValues: Partial<NumericQuantityDto> = {
  id: NIL,
  name: t('Untitled'),
  isAvailable: true,
  setupUsage: {
    id: NIL,
    isUsageEnabled: false,
    medicalSystemScope: [],
    qaEquipmentScope: [],
    setupGeometries: [],
    useSetupGeometry: false,
    decimalPlaces: 3,
    valueType: 'FloatingPoint',
  },
  evaluationUsage: {
    medicalSystemScope: [],
    qaEquipmentScope: [],
    evaluationGeometries: [],
    definitions: [],
    useEvaluationGeometry: false,
    isUsageEnabled: false,
  },
};

const { form } = useVeeForm<NumericQuantityDto>({
  schemaName: 'NumericQuantityDto',
  options: { initialValues },
  mode: 'add',
  customValidateFunctions: [
    backendValidator((numericQuantityDto, errorHandlers) =>
      api.numericQuantitiesApi.validateNumericQuantity(
        {
          createUpdateNumericQuantityDto: toCreateUpdateNumericQuantityDto(numericQuantityDto),
        },
        errorHandlers,
      ),
    ),
  ],
});

const { handleSubmit, values } = form;

const submit = handleSubmit(async (numericQuantityDto) => {
  const { success, result: numericQuantity } = await api.numericQuantitiesApi.createNumericQuantity(
    {
      createUpdateNumericQuantityDto: toCreateUpdateNumericQuantityDto(numericQuantityDto),
    },
    [formBackendErrorHandler(form)],
  );

  if (success) {
    notificationStore.success(
      t('EntityAdded', {
        name: numericQuantity.name,
        iuid: numericQuantity.iuid,
      }),
    );
    await router.push({
      name: routenames.quantities.overview,
    });
  }
});
</script>

<template>
  <form @submit="submit">
    <BasePage>
      <template #title>
        <BasePageTitle :subtitle="$t('Quantity_one')" :title="values.name" />
      </template>

      <template #status>
        <BasePageStatus />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.quantities.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>

      <QuantityForm />
    </BasePage>
  </form>
</template>
